import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { PhoneInput } from '../components/PhoneInput';
import { OTPInput } from '../components/OTPInput';
import { ArrowLeft, X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { loadTurnstileScript, resolveApiBaseUrl } from '../lib/utils';

type AuthVariant = 'page' | 'modal'

interface ForgotPasswordScreenProps {
  variant?: AuthVariant
  onClose?: () => void
  onBack?: () => void
  onNext?: () => void
}

export const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({
  variant = 'page',
  onClose,
  onBack,
  onNext
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);

  const [turnstileState, setTurnstileState] = useState<{ status: 'loading' | 'enabled' | 'disabled'; siteKey: string }>(
    { status: 'loading', siteKey: '' }
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    let active = true;
    const fetchTurnstileConfig = async () => {
      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/auth/turnstile`);
        const payload = await response.json().catch(() => null);
        if (!active) return;
        if (response.ok && payload?.enabled && payload?.site_key) {
          setTurnstileState({ status: 'enabled', siteKey: payload.site_key });
          setTurnstileError(null);
        } else {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      } catch (error) {
        if (active) {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      }
    };
    fetchTurnstileConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (turnstileState.status !== 'enabled' || !turnstileState.siteKey || !turnstileRef.current) {
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileRef.current || !window.turnstile) {
          return;
        }
        if (turnstileWidgetId.current && window.turnstile.remove) {
          window.turnstile.remove(turnstileWidgetId.current);
          turnstileWidgetId.current = null;
        }
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileState.siteKey,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError(null);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': () => {
            setTurnstileToken(null);
            setTurnstileError(t('captchaError'));
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileError(t('captchaLoadFailed'));
        }
      });
    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileState.status, turnstileState.siteKey, t]);

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken(null);
  };

  const handleSendCode = async () => {
    if (loading) return;
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    if (!trimmedPhone) {
      setSendError(t('phoneRequired'));
      return;
    }
    if (trimmedPhone.length !== 11) {
      setSendError(t('phoneInvalid'));
      return;
    }
    if (turnstileState.status === 'loading') {
      setTurnstileError(t('captchaLoading'));
      return;
    }
    if (turnstileState.status !== 'enabled') {
      setTurnstileError(t('captchaUnavailable'));
      return;
    }
    if (!turnstileToken) {
      setTurnstileError(t('captchaRequired'));
      return;
    }

    setLoading(true);
    setSendError(null);
    setDebugCode(null);
    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: trimmedPhone,
          turnstile_token: turnstileState.status === 'enabled' ? turnstileToken : undefined
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const retryAfter = Number(response.headers.get('Retry-After') || 0);
        if (response.status === 429 && retryAfter > 0) {
          setCountdown(retryAfter);
        }
        setSendError(payload?.detail || payload?.message || t('sendCodeFailed'));
        setLoading(false);
        resetTurnstile();
        return;
      }

      setLoading(false);
      setStep('verify');
      setCountdown(60);
      setOtpError(null);
      setOtpSuccess(false);
      setOtpValue('');
      setTurnstileError(null);
      if (payload?.debug_code) {
        setDebugCode(String(payload.debug_code));
      }
      resetTurnstile();
    } catch (error) {
      setSendError(t('sendCodeFailed'));
      setLoading(false);
      resetTurnstile();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'phone') {
      handleSendCode();
      return;
    }
    handleVerify();
  };

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    navigate('/login')
  }

  const handleVerify = (value?: string) => {
    if (loading || otpSuccess) return;
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    const code = value ?? otpValue;
    if (!code || code.length !== 6) {
      setOtpError(t('invalidCode'));
      return;
    }
    if (!trimmedPhone || trimmedPhone.length !== 11) {
      setOtpError(t('phoneInvalid'));
      return;
    }
    setLoading(true);
    setOtpError(null);
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(
          'auth_reset_context',
          JSON.stringify({
            phone: trimmedPhone,
            code,
            created_at: Date.now()
          })
        );
      }
    } catch (error) {
      // ignore storage errors
    }
    setOtpSuccess(true);
    setLoading(false);
    window.setTimeout(() => {
      if (onNext) {
        onNext();
        return;
      }
      navigate('/reset-password');
    }, 500);
  };

  const handleOtpChange = (value: string) => {
    setOtpValue(value);
    if (otpError) setOtpError(null);
    if (otpSuccess) setOtpSuccess(false);
  };

  const canSend = phone.trim().length >= 6;
  const canVerify = otpValue.length === 6 && !loading;
  const isModal = variant === 'modal';
  const steps = useMemo(() => [t('resetStep1'), t('resetStep2'), t('resetStep3')], [t]);
  const activeStep = step === 'phone' ? 0 : 1;

  return (
    <div
      data-auth-variant={variant}
      className={isModal ? 'w-full' : 'min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4'}
    >
    <div
      className={`relative w-full bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 p-10 md:p-12 ${
        isModal ? '' : 'max-w-[440px]'
      }`}
    >
      {isModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
      <div className="flex flex-col items-center text-center mb-8">
        <div className="mb-8">
          <Logo />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">{t('resetTitle')}</h1>
        <p className="text-slate-500 text-sm leading-relaxed max-w-[300px]">
          {t('resetSubtitle')}
        </p>
      </div>

      <div className="flex items-center justify-center gap-3 mb-8">
        {steps.map((label, index) => (
          <div key={label} className="flex items-center gap-3">
            <div
              className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${
                index <= activeStep ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'
              }`}
            >
              {index + 1}
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${index === activeStep ? 'text-slate-900' : 'text-slate-400'}`}>
              {label}
            </span>
            {index < steps.length - 1 && (
              <div className={`w-8 h-px ${index < activeStep ? 'bg-primary' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {step === 'phone' && (
          <>
            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                {t('phoneLabel')}
              </label>
              <PhoneInput
                placeholder={t('mobilePlaceholder')}
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  if (sendError) setSendError(null);
                }}
                autoFocus
              />
            </div>
          </>
        )}

        {step === 'phone' && (
          <div className="space-y-2" data-turnstile-container="true">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
              {t('captchaLabel')}
            </label>
            <div className="relative rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-center min-h-[72px]">
              <div ref={turnstileRef} className="w-full flex justify-center" />
              {turnstileState.status === 'loading' && (
                <span className="absolute text-xs text-slate-400">{t('captchaLoading')}</span>
              )}
              {turnstileState.status === 'disabled' && (
                <span className="absolute text-xs text-slate-400">{t('captchaUnavailable')}</span>
              )}
            </div>
            <p className="text-xs text-slate-500">{t('captchaHint')}</p>
            {turnstileError && (
              <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                <AlertCircle size={14} />
                <span className="text-xs font-medium">{turnstileError}</span>
              </div>
            )}
          </div>
        )}

        {step === 'phone' && (
          <>
            <button
              type="submit"
              disabled={!canSend || loading}
              className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : t('sendReset')}
            </button>
            {sendError && (
              <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                <AlertCircle size={14} />
                <span className="text-xs font-medium">{sendError}</span>
              </div>
            )}
          </>
        )}

        {step === 'verify' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-900 uppercase tracking-widest">{t('resetStep2')}</p>
                <p className="text-[11px] text-slate-400 mt-1">{t('resetCodeSent')}</p>
              </div>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={countdown > 0 || loading}
                className={`text-[11px] font-bold uppercase tracking-widest ${
                  countdown > 0 || loading ? 'text-slate-300 cursor-default' : 'text-primary hover:text-blue-700'
                }`}
              >
                {countdown > 0 ? t('resendIn').replace('{time}', countdown.toString()) : t('resendNow')}
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                {t('resetCodeHint')}
              </label>
              <div className="flex items-center gap-3">
                <OTPInput
                  length={6}
                  onChange={handleOtpChange}
                  onComplete={(code) => {
                    setOtpValue(code);
                    if (code.length === 6) {
                      handleVerify(code);
                    }
                  }}
                  error={!!otpError}
                  success={otpSuccess}
                  readOnly={otpSuccess}
                />
                {otpSuccess && (
                  <div className="flex-shrink-0 text-success animate-in zoom-in duration-300">
                    <CheckCircle2 size={26} strokeWidth={2.5} className="fill-success/10" />
                  </div>
                )}
              </div>
              {otpError && (
                <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle size={14} />
                  <span className="text-xs font-medium">{otpError}</span>
                </div>
              )}
              {sendError && !otpError && (
                <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle size={14} />
                  <span className="text-xs font-medium">{sendError}</span>
                </div>
              )}
              {debugCode && (
                <div className="text-xs text-slate-500">
                  {t('debugCodeHint').replace('{code}', debugCode)}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={!canVerify}
              className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : t('resetVerifyAction')}
            </button>

            <button
              type="button"
              onClick={() => setStep('phone')}
              className="w-full text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-primary transition-colors"
            >
              {t('resetBackStep')}
            </button>
          </div>
        )}
      </form>

      <div className="text-center mt-10">
        <button
          onClick={handleBack}
          className="text-sm text-slate-500 font-medium hover:text-primary transition-colors flex items-center justify-center gap-2 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {t('backLogin')}
        </button>
      </div>
    </div>
    </div>
  );
};
